<template>
    <CollapsibleWidget collapsed-height="47" :is-opened="isOpen" @onChange="toggleOpen">
        <template #name>{{ editable.label }}</template>

        <template #controls>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>

        <div class="deliverable-values editable-widget__body">
            <div v-if="readOnly" class="editable-widget__body--readonly" title="Read Only" @click.stop.prevent></div>
            <ExpandableTextInput
                class="deliverable-value-item"
                :label="EditableGroupScopeLabel"
                :is-expanded="isExpanded(null)"
                type="textarea"
                :model-value="getModelValue(editable, groupsAffectingEditable, null)"
                :settings="getEditableSettings(editable, groupsAffectingEditable, null)"
                :scope="OverwriteScope.EditableGroup"
                :overwrite="getOverwrite(editable, groupsAffectingEditable, null)"
                :placeholder="groupPlaceholder"
                :lockable="false"
                @collapse="onCollapse(null)"
                @input="throttledValueChange"
                @expand="onExpand(editable, groupsAffectingEditable, null)"
                @restore="onValueRestore(null)"
                @updateSettings="onUpdateSettings({ ...$event, templateId: null })"
            />
            <div v-if="!editable.publisherPlatform">
                <ExpandableTextInput
                    v-for="(label, templateId) in templateLabels"
                    v-show="isOpen"
                    :key="templateId"
                    class="deliverable-value-item"
                    :is-expanded="isExpanded(templateId)"
                    :label="label"
                    :label-color="getLabelColor(editable, groupsAffectingEditable, templateId)"
                    type="textarea"
                    :model-value="getModelValue(editable, groupsAffectingEditable, templateId)"
                    :settings="getEditableSettings(editable, groupsAffectingEditable, templateId)"
                    :scope="OverwriteScope.EditableGroupTemplate"
                    :overwrite="getOverwrite(editable, groupsAffectingEditable, templateId)"
                    placeholder="Enter value"
                    @collapse="onCollapse(templateId)"
                    @input="throttledValueChange($event, templateId)"
                    @expand="onExpand(editable, groupsAffectingEditable, templateId)"
                    @removeOverwrite="onRemoveOverwrite"
                    @restore="onValueRestore(templateId)"
                    @updateSettings="onUpdateSettings({ ...$event, templateId })"
                />
            </div>
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts">
import throttle from "lodash.throttle";
import { OverwriteScope } from "hoxton-shared/enums/overwrites";
import type { CSSSettings, Editable } from "hoxton-types";
import { defineComponent, toRef, type PropType } from "vue";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import ExpandableTextInput from "@/components/Campaign/ExpandableTextInput.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";

import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useEditable } from "@/composables/useEditable";
import { useEditor } from "@/composables/useEditor";

import editableUtils from "@/utils/editables";
import type { EditedEditable } from "@/types/types";
import { EditableGroupScopeLabel } from "@/enums/overwrites";
import { EditorAction } from "@/store/modules/editor";

export default defineComponent({
    name: "EditableTextWidget",
    components: {
        CollapsibleWidget,
        EditableRestrictionControl,
        ExpandableTextInput
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { getOverwrite, removeOverwrite } = useOverwrite();
        const { getEditableValue, getLabelColor, update, restore, getEditableSettings, updateSettings } =
            useEditableMethods({ emit });
        const { templateLabels } = useEditable({ editable });
        const { selectedLanguage } = useEditor();

        return {
            isOpen,
            toggleOpen,
            getOverwrite,
            getEditableSettings,
            getEditableValue,
            getLabelColor,
            templateLabels,
            updateSettings,
            update,
            restore,
            removeOverwrite,
            selectedLanguage
        };
    },

    data() {
        return {
            EditableGroupScopeLabel,
            OverwriteScope,
            throttledValueChange: Function as (value: unknown, templateId?: string) => void
        };
    },

    computed: {
        editedEditable() {
            return this.$store.state.editor.editedEditable as unknown as EditedEditable;
        },

        groupPlaceholder() {
            const masterTemplateIds = Object.keys(this.templateLabels);
            return editableUtils.getGroupPlaceholder(this.editable, masterTemplateIds, "Enter value");
        },

        isEdited() {
            return this.editedEditable && this.editedEditable.editable._id === this.editable._id;
        }
    },

    created() {
        this.throttledValueChange = throttle((value, templateId) => {
            this.onValueChange(value, templateId);
        }, 100);
    },

    methods: {
        getModelValue(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            return this.getEditableValue(editable, groupsAffectingEditable, templateId) as string | null;
        },

        isExpanded(templateId: string | null) {
            return this.isEdited && this.editedEditable.masterTemplateId === templateId;
        },

        onCollapse(templateId: string | null) {
            if (this.isExpanded(templateId)) {
                this.$store.dispatch(EditorAction.SetEditedEditable, null);
            }
        },

        onExpand(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            this.$store.dispatch(EditorAction.SetEditedEditable, {
                editable,
                masterTemplateId: templateId,
                groupsAffectingEditable
            });

            this.$store.dispatch(EditorAction.SetSettingsPaneVisibility, true);
        },

        onRemoveOverwrite(overwriteId: string) {
            this.removeOverwrite(overwriteId);
        },

        onValueChange(value: string, templateId: string | null) {
            this.update({
                editable: this.editable,
                updateData: {
                    value
                },
                groupsAffectingEditable: this.groupsAffectingEditable,
                templateId
            });
        },

        onValueRestore(templateId: string | null) {
            this.restore({
                editable: this.editable,
                templateId,
                language: this.selectedLanguage
            });
        },

        onUpdateSettings({
            settings,
            newSettings,
            templateId
        }: {
            settings: CSSSettings;
            newSettings: CSSSettings;
            templateId: string | null;
        }) {
            const mergedSettings = {
                ...settings,
                ...newSettings
            };
            this.updateSettings({
                editable: this.editable,
                settings: mergedSettings,
                templateId,
                groupsAffectingEditable: this.groupsAffectingEditable
            });
        }
    }
});
</script>
